import React, { useEffect, useState } from 'react';
import { db, collection, onSnapshot, query, orderBy, handleFirestoreError, OperationType, deleteDoc, doc } from '../firebase';
import { Feedback } from '../types';
import { useAuth } from '../App';
import { 
  Star, 
  MessageSquare, 
  Trash2, 
  Calendar, 
  Building2, 
  Search,
  Quote,
  Printer
} from 'lucide-react';
import { motion } from 'motion/react';
import ConfirmationModal from '../components/ConfirmationModal';

const FeedbackPage: React.FC = () => {
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { isAdmin, isManager, isCustomerCare } = useAuth();

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    const q = query(collection(db, 'feedback'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setFeedbackList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Feedback)));
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'feedback');
    });

    return unsubscribe;
  }, []);

  const handleDelete = (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Feedback',
      message: 'Are you sure you want to delete this feedback comment?',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'feedback', id));
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, `feedback/${id}`);
        }
      }
    });
  };

  const filteredFeedback = feedbackList.filter(f => 
    (f.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
    f.comment?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const averageRating = feedbackList.length > 0 
    ? (feedbackList.reduce((acc, curr) => acc + curr.rating, 0) / feedbackList.length).toFixed(1)
    : '0.0';

  if (loading) return <div className="flex items-center justify-center h-64">Loading feedback...</div>;

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Client Feedback</h1>
          <p className="text-slate-500">Monitor service quality and client satisfaction</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-6 py-3 bg-white text-blue-900 rounded-2xl font-bold border border-blue-100 shadow-sm hover:bg-blue-50 transition-all print:hidden"
          >
            <Printer className="w-5 h-5" />
            Print Feedback
          </button>
          <div className="flex items-center gap-4 bg-white p-4 rounded-3xl border border-blue-100 shadow-sm shadow-blue-900/5 print:hidden">
            <div className="text-center px-4 border-r border-slate-100">
              <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Average Rating</p>
              <div className="flex items-center justify-center gap-1 mt-1">
                <span className="text-2xl font-black text-blue-900">{averageRating}</span>
                <Star className="w-5 h-5 text-orange-500 fill-current" />
              </div>
            </div>
            <div className="text-center px-4">
              <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Total Reviews</p>
              <p className="text-2xl font-black text-blue-900 mt-1">{feedbackList.length}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-6 print:border-none print:shadow-none print:p-0">
        <div className="relative print:hidden">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search feedback by client or comment..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredFeedback.map((feedback) => (
            <motion.div
              key={feedback.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white border border-slate-100 rounded-3xl p-6 hover:shadow-xl hover:shadow-slate-100 transition-all relative overflow-hidden group"
            >
              <Quote className="absolute -right-4 -top-4 w-24 h-24 text-slate-50 opacity-50 group-hover:text-slate-100 transition-colors" />
              
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-4 h-4 ${
                          star <= feedback.rating ? 'text-orange-500 fill-current' : 'text-slate-200'
                        }`}
                      />
                    ))}
                  </div>
                  {(isAdmin || isManager) && (
                    <button
                      onClick={() => handleDelete(feedback.id)}
                      className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  <p className="text-slate-700 italic leading-relaxed">
                    "{feedback.comment || 'No comment provided'}"
                  </p>

                  <div className="pt-4 border-t border-slate-50 flex flex-col gap-2">
                    <div className="flex items-center gap-2 text-sm font-black text-blue-900 tracking-tight">
                      <Building2 className="w-4 h-4 text-blue-400" />
                      {feedback.clientName}
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {feedback.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                      </div>
                      <div className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-100">
                        Task ID: {feedback.taskId.slice(-6)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredFeedback.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-8 h-8 text-slate-300" />
            </div>
            <p className="text-slate-500 font-medium">No feedback found matching your search</p>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel="YES"
        cancelLabel="NO"
      />
    </div>
  );
};

export default FeedbackPage;
